/**
 * @CODE:ACC-003-HEADER-001
 * AccommodationHeader Component
 *
 * Header section for accommodation detail page:
 * - Accommodation name, type, and location
 * - Pricing information
 * - Contact phone (clickable tel: link)
 * - Website link
 * - Favorite button with toggle
 * - Share dialog button
 * - Responsive layout
 * - Full accessibility support
 */

import { Heart, Share2, ExternalLink, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useFavorite, useShareDialog } from "../hooks";
import type { AccommodationWithDetails } from "../types";
import { cleanDescriptionText } from "../utils/textFormatters";

interface AccommodationHeaderProps {
  /** Accommodation data to display */
  accommodation: AccommodationWithDetails;
  /** Optional CSS class */
  className?: string;
}

/**
 * Format price as Korean Won
 */
function formatPrice(price: number): string {
  return new Intl.NumberFormat("ko-KR", {
    style: "currency",
    currency: "KRW",
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format location as "Province District"
 */
function formatLocation(
  province: string,
  district: string
): string {
  return `${province} ${district}`;
}

/**
 * Detect map service type from URL
 */
function getMapServiceType(url: string): "kakao" | "naver" | "other" {
  if (!url) return "other";

  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes("kakao") || lowerUrl.includes("daum")) {
    return "kakao";
  }
  if (lowerUrl.includes("naver")) {
    return "naver";
  }
  return "other";
}

/**
 * Get map service label
 */
function getMapServiceLabel(type: "kakao" | "naver" | "other"): string {
  switch (type) {
    case "kakao":
      return "카카오맵";
    case "naver":
      return "네이버지도";
    default:
      return "지도";
  }
}

/**
 * Header component showing basic accommodation information
 * with favorite, share, and contact actions
 */
export const AccommodationHeader = ({
  accommodation,
  className,
}: AccommodationHeaderProps) => {
  const { isFavorite, toggleFavorite } = useFavorite(
    accommodation.accommodation_id
  );
  const { openDialog: openShareDialog } = useShareDialog(accommodation);

  return (
    <div className={cn("w-full bg-white", className)}>
      <div className="container mx-auto px-4 py-2 sm:py-4">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Left: Name, Type, Location */}
          <div className="space-y-3">
            {/* Type Badge */}
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {accommodation.accommodation_type.name}
              </Badge>
            </div>

            {/* Name */}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {accommodation.name}
            </h1>

            {/* Location */}
            <div className="text-sm text-gray-600">
              <p>
                {formatLocation(
                  accommodation.province,
                  accommodation.district
                )}
              </p>
              <p className="text-xs mt-1">{accommodation.address}</p>
            </div>

            {/* Website & Map Links */}
            <div className="flex gap-2 pt-2">
              {accommodation.website && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <a
                    href={accommodation.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="웹사이트 방문"
                  >
                    <ExternalLink className="h-4 w-4" />
                    웹사이트
                  </a>
                </Button>
              )}

              {accommodation.map_url && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <a
                    href={accommodation.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${getMapServiceLabel(getMapServiceType(accommodation.map_url))} 열기`}
                  >
                    <MapPin className="h-4 w-4" />
                    {getMapServiceLabel(getMapServiceType(accommodation.map_url))}
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Right: Price and Actions */}
          <div className="space-y-4">
            {/* Price */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">1박 기준 요금</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(accommodation.price_from)}
              </p>
              <p className="text-xs text-gray-500 mt-1">부터</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant={isFavorite ? "default" : "outline"}
                size="sm"
                onClick={toggleFavorite}
                className="flex-1 gap-2"
                aria-label={
                  isFavorite ? "찜 취소" : "이 숙소 찜하기"
                }
              >
                <Heart
                  className={cn(
                    "h-4 w-4",
                    isFavorite && "fill-current"
                  )}
                />
                <span className="hidden sm:inline">
                  {isFavorite ? "찜 취소" : "찜하기"}
                </span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={openShareDialog}
                className="flex-1 gap-2"
                aria-label="공유"
              >
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">공유</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Description - Full Width Below Grid */}
        {accommodation.description && (
          <div className="mt-6">
            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
              {cleanDescriptionText(accommodation.description)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
